import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import MatchupCard from '../components/ui/MatchupCard';
import SectionHeader from '../components/ui/SectionHeader';
import { currentWeek, loadWeek } from '../utils/weekData';
import predictionIssues from '../data/predictionIssues.json';
import { parseCSV } from '../utils/csv';
import { loadCareerRankings } from '../utils/managerStats';
import { loadRoasts, roastsByCategories } from '../utils/roasts';
import { loadSchedule, matchupsForWeek } from '../utils/schedule';
import { buildWeekOneRivalryPreviews } from '../utils/rivalryPreview';
import { CURRENT_SEASON, hasPublishedSchedule, isPreDraftSeason } from '../config/season';
import WeekOneRivalryPreview from '../components/weekly/WeekOneRivalryPreview';
import { CastFigure, Reveal } from '../motion';

export default function Weekly() {
  const params = useParams();
  const requestedYear = Number.parseInt(params.year, 10);
  const isHistoricalRequest = Number.isInteger(requestedYear) && requestedYear < CURRENT_SEASON.year;

  const forecast = !params.year && !params.weekSlug
    ? predictionIssues.latest
    : predictionIssues.issues.find((issue) => issue.season === requestedYear && params.weekSlug === `week-${issue.week}`);
  if (forecast) {
    return <Navigate to={`/ai-picks/${forecast.season}/week-${forecast.week}`} replace />;
  }

  if (isPreDraftSeason() && !isHistoricalRequest) {
    return <PreDraftWeekly />;
  }

  return <WeeklyPreview />;
}

function WeeklyPreview() {
  const params = useParams();
  const weekMatch = params.weekSlug ? params.weekSlug.match(/^week-(\d+)$/) : null;
  const resolvedWeek = params.year && weekMatch
    ? { year: parseInt(params.year, 10), week: parseInt(weekMatch[1], 10) }
    : currentWeek();

  if (!resolvedWeek) {
    return <NoPublishedWeek />;
  }

  return <WeeklyDataPreview year={resolvedWeek.year} week={resolvedWeek.week} />;
}

function NoPublishedWeek() {
  return (
    <HeroBackground backdrop="studio" className="px-6 md:px-14 py-20">
      <Eyebrow>WEEKLY</Eyebrow>
      <h1 className="font-display font-black uppercase tracking-normal mt-3.5 leading-[0.88]" style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>No published week yet.</h1>
      <p className="mt-4 max-w-xl text-sm md:text-base text-ink-secondary leading-relaxed italic">
        Weekly previews need a real schedule and a real week number before this page can load matchup data.
      </p>
    </HeroBackground>
  );
}

function WeeklyDataPreview({ year, week }) {
  const [data, setData] = useState(undefined);
  const [wireFacts, setWireFacts] = useState([]);
  useEffect(() => {
    setData(undefined);
    loadWeek(year, week).then(setData).catch(() => setData(null));
  }, [year, week]);
  useEffect(() => {
    loadRoasts()
      .then((roasts) => setWireFacts(roastsByCategories(roasts, [
        { category: 'heartbreaks', label: 'Heartbreak', limit: 1 },
        { category: 'blowouts', label: 'Blowout', limit: 1 },
        { category: 'streaks', label: 'Streak', limit: 1 },
        { category: 'rivalry_extremes', label: 'Blood Feud', limit: 1 },
      ])))
      .catch(() => setWireFacts([]));
  }, []);

  if (data === undefined) {
    return (
      <HeroBackground backdrop="studio" className="px-6 md:px-14 py-20">
        <Eyebrow>WEEKLY</Eyebrow>
        <h1 className="font-display font-black uppercase tracking-normal mt-3.5 leading-[0.88]" style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>Loading week {week}…</h1>
      </HeroBackground>
    );
  }

  if (data === null) {
    return (
      <HeroBackground backdrop="studio" className="px-6 md:px-14 py-20">
        <Eyebrow>WEEKLY</Eyebrow>
        <h1 className="font-display font-black uppercase tracking-normal mt-3.5 leading-[0.88]" style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>Week {week} is not published.</h1>
        <p className="mt-4 max-w-xl text-sm md:text-base text-ink-secondary leading-relaxed italic">
          No weekly file exists for {year} Week {week}. The site will not invent matchups,
          projections, or storylines without a real data file.
        </p>
      </HeroBackground>
    );
  }

  return (
    <>
      {/* Week selector */}
      <div className="bg-bg-deep px-6 md:px-9 py-5 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-b border-border">
        <div className="text-[10px] text-ink-tertiary tracking-[0.2em] whitespace-nowrap">← DRAFT RECAP</div>
        <div className="text-center min-w-0">
          <div className="text-[9px] text-accent-gold tracking-[0.4em] font-black mb-1">{data.date_label}</div>
          <div className="text-2xl md:text-3xl font-black tracking-normal leading-none">WEEK {data.week} <span className="text-accent-gold">PREVIEW</span></div>
        </div>
        <div className="text-[10px] text-ink-tertiary tracking-[0.2em] whitespace-nowrap">WEEK {data.week + 1} →</div>
      </div>

      {/* Lead Story */}
      <HeroBackground variant="red" backdrop="studio">
        <div className="px-6 md:px-14 py-16 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-12 items-center">
          <div>
            <Eyebrow color="red">{data.lead.eyebrow}</Eyebrow>
            <h1 className="font-display font-black uppercase leading-[0.88] tracking-normal mt-3.5 mb-5" style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>
              {data.lead.headline}<br /><span className="text-accent-gold">{data.lead.subhead}</span>
            </h1>
            <p className="text-base text-ink-secondary leading-relaxed max-w-xl mb-6 font-light">{data.lead.blurb}</p>
            <div className="flex flex-wrap gap-3.5">
              <span className="text-[11px] text-bg-base font-black tracking-[0.25em] bg-accent-gold px-5 py-3.5">READ THE FULL PREVIEW →</span>
              <span className="text-[11px] text-accent-gold font-extrabold tracking-[0.25em] border-[1.5px] border-accent-gold px-5 py-3">5 STORYLINES TO WATCH</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            {[data.lead.home, data.lead.away].map((mgr, i) => (
              <div key={mgr} className="paper relative aspect-[4/5] border border-accent-gold/50">
                <CastFigure
                  name={mgr}
                  trigger="inview"
                  sequence={['shock', 'point']}
                  className="absolute inset-0 w-full h-full"
                />
                <div className={`absolute z-10 bottom-3.5 ${i === 0 ? 'left-3.5' : 'right-3.5 text-right'}`}>
                  <div className="text-[9px] text-accent-gold tracking-[0.3em] font-black">{i === 0 ? 'DEFENDING CHAMP' : '3× CHAMP'}</div>
                  <div className="text-2xl md:text-3xl font-black tracking-normal text-bg-base bg-cream/90 px-2 py-1">{mgr.toUpperCase()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </HeroBackground>

      {/* Deep dive */}
      <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-deep">
        <Reveal><SectionHeader eyebrow="MATCHUP DEEP DIVE" title={`${data.deep_dive.home.name.toUpperCase()} vs ${data.deep_dive.away.name.toUpperCase()} · The numbers.`} /></Reveal>
        <Reveal delay={80} className="bg-bg-surface p-5 md:p-8 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_80px_minmax(0,1fr)] gap-6 items-center overflow-hidden">
          {/* Home side */}
          <div className="md:text-right min-w-0">
            <div className="flex flex-row-reverse md:flex-row md:justify-end items-center gap-3.5 mb-6 min-w-0">
              <div className="min-w-0">
                <div className="text-2xl md:text-3xl font-black leading-none tracking-normal break-words">{data.deep_dive.home.name.toUpperCase()}</div>
                <div className="text-[10px] text-accent-gold tracking-[0.2em] font-bold leading-tight break-words mt-1">{data.deep_dive.home.tagline}</div>
              </div>
              <ManagerAvatar name={data.deep_dive.home.name} size="lg" framed />
            </div>
            <div className="space-y-3.5">
              <div><div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">CAREER RECORD</div><div className="text-xl font-black text-accent-gold">{data.deep_dive.home.career_record}</div></div>
              <div><div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">CHAMPIONSHIPS</div><div className="text-xl font-black text-accent-gold">{data.deep_dive.home.rings}</div></div>
              <div><div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">VS OPPONENT</div><div className="text-xl font-black">{data.deep_dive.home.head_to_head}</div></div>
              <div><div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">{year - 1} FINISH</div><div className="text-xl font-black">{data.deep_dive.home.last_finish}</div></div>
            </div>
          </div>

          <div className="text-center text-5xl font-black text-accent-gold tracking-widest">VS</div>

          {/* Away side */}
          <div className="min-w-0">
            <div className="flex items-center gap-3.5 mb-6 min-w-0">
              <ManagerAvatar name={data.deep_dive.away.name} size="lg" framed />
              <div className="min-w-0">
                <div className="text-2xl md:text-3xl font-black leading-none tracking-normal break-words">{data.deep_dive.away.name.toUpperCase()}</div>
                <div className="text-[10px] text-accent-gold tracking-[0.2em] font-bold leading-tight break-words mt-1">{data.deep_dive.away.tagline}</div>
              </div>
            </div>
            <div className="space-y-3.5">
              <div><div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">CAREER RECORD</div><div className="text-xl font-black text-accent-gold">{data.deep_dive.away.career_record}</div></div>
              <div><div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">CHAMPIONSHIPS</div><div className="text-xl font-black text-accent-gold">{data.deep_dive.away.rings}</div></div>
              <div><div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">VS OPPONENT</div><div className="text-xl font-black">{data.deep_dive.away.head_to_head}</div></div>
              <div><div className="text-[9px] text-ink-tertiary tracking-[0.2em] font-bold">{year - 1} FINISH</div><div className="text-xl font-black">{data.deep_dive.away.last_finish}</div></div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={140} className="grid grid-cols-1 md:grid-cols-3 gap-3.5 mt-3.5">
          {[
            { ...data.deep_dive.pick, label: 'THE PICK', color: 'gold' },
            { ...data.deep_dive.key_matchup, label: 'THE KEY MATCHUP', color: 'gold' },
            { ...data.deep_dive.x_factor, label: 'THE X-FACTOR', color: 'red' },
          ].map((card) => (
            <div key={card.label} className={`bg-bg-surface p-6 border-t-[3px] ${card.color === 'gold' ? 'border-accent-gold' : 'border-accent-red'}`}>
              <div className={`text-[9px] tracking-[0.25em] font-extrabold mb-2.5 ${card.color === 'gold' ? 'text-accent-gold' : 'text-accent-red'}`}>▾ {card.label}</div>
              <div className="text-sm font-black mb-1.5">{card.title}</div>
              <div className="text-[11px] text-ink-tertiary italic leading-relaxed">{card.blurb}</div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Full Card */}
      <section className="px-6 md:px-14 py-14 border-b border-border">
        <Reveal><SectionHeader eyebrow="THE FULL CARD" title={`All published Week ${data.week} matchups.`} /></Reveal>
        <Reveal delay={80} className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          {data.matchups.map((m, i) => (
            <MatchupCard
              key={i}
              label={m.label}
              home={{ name: m.home, subtitle: m.home_subtitle }}
              away={{ name: m.away, subtitle: m.away_subtitle }}
              storyline={m.storyline}
              projection={m.projection}
            />
          ))}
        </Reveal>
      </section>

      {/* Almanac Wire */}
      {wireFacts.length > 0 && (
        <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-base">
          <Reveal><SectionHeader
              eyebrow="THE ALMANAC WIRE"
              title="Four archived reasons to be nervous."
              eyebrowColor="red"
            /></Reveal>
          <Reveal delay={80} className="grid grid-cols-1 md:grid-cols-4 gap-3.5">
            {wireFacts.map((fact, i) => (
              <article
                key={`${fact.category}-${i}`}
                className={`bg-bg-surface p-6 border-t-[3px] ${
                  fact.category === 'blowouts' || fact.category === 'heartbreaks'
                    ? 'border-accent-red'
                    : 'border-accent-gold'
                }`}
              >
                <div className={`text-[9px] tracking-[0.25em] font-extrabold mb-3 uppercase ${
                  fact.category === 'blowouts' || fact.category === 'heartbreaks'
                    ? 'text-accent-red'
                    : 'text-accent-gold'
                }`}>
                  {fact.label}
                </div>
                <p className="text-[12px] md:text-[13px] text-ink-secondary leading-relaxed italic">
                  {fact.text}
                </p>
              </article>
            ))}
          </Reveal>
        </section>
      )}

      {/* Power Movement */}
      <section className="px-6 md:px-14 py-14 bg-bg-deep border-b border-border">
        <Reveal><SectionHeader eyebrow="THE BAROMETER" title={`Week ${data.week} power movement.`} /></Reveal>
        <div className="grid grid-cols-[32px_40px_minmax(0,1fr)_52px_40px] md:grid-cols-[60px_80px_minmax(0,1fr)_100px_80px] gap-2 md:gap-3.5 items-center pb-3 border-b border-border text-[9px] md:text-[10px] text-ink-tertiary tracking-[0.12em] md:tracking-[0.2em]">
          <div>RANK</div><div></div><div>MANAGER</div><div>POWER</div><div>CHANGE</div>
        </div>
        {data.power_movement.map((row) => {
          const changeNum = parseInt(row.change, 10);
          const changeColor = changeNum > 0 ? 'text-emerald-500' : changeNum < 0 ? 'text-accent-red' : 'text-ink-tertiary';
          const changeText = changeNum > 0 ? `↑ ${changeNum}` : changeNum < 0 ? `↓ ${-changeNum}` : '—';
          return (
            <div key={row.manager} className="grid grid-cols-[32px_40px_minmax(0,1fr)_52px_40px] md:grid-cols-[60px_80px_minmax(0,1fr)_100px_80px] gap-2 md:gap-3.5 items-center py-3.5 border-b border-border last:border-0">
              <div className={`text-xl font-black ${row.rank <= 2 ? 'text-accent-gold' : ''}`}>{row.rank}</div>
              <ManagerAvatar name={row.manager} size="sm" />
              <div className="min-w-0">
                <div className="text-sm font-black truncate">{row.manager.toUpperCase()}</div>
                <div className="text-[9px] text-ink-tertiary tracking-[0.15em] truncate">{row.blurb}</div>
              </div>
              <div className={`text-base font-black ${row.rank <= 2 ? 'text-accent-gold' : ''}`}>{row.power}</div>
              <div className={`text-sm font-black ${changeColor}`}>{changeText}</div>
            </div>
          );
        })}
      </section>

      {/* Around the league */}
      <section className="px-6 md:px-14 py-14 border-b border-border">
        <Reveal><SectionHeader eyebrow="AROUND THE LEAGUE" title="Five things to know." /></Reveal>
        <Reveal delay={80} className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
          {data.around_the_league.map((item, i) => (
            <div key={i} className={`bg-bg-surface p-6 ${item.accent === 'red' ? 'border-l-[3px] border-accent-red' : ''}`}>
              <div className={`text-[9px] tracking-[0.25em] font-extrabold mb-3 ${item.accent === 'red' ? 'text-accent-red' : 'text-accent-gold'}`}>{item.label}</div>
              <div className="text-lg font-black leading-tight mb-2.5 tracking-normal">{item.title}</div>
              <div className="text-[11px] text-ink-tertiary leading-relaxed">{item.blurb}</div>
            </div>
          ))}
        </Reveal>
      </section>
    </>
  );
}

function PreDraftWeekly() {
  const [schedule, setSchedule] = useState(undefined);
  const [rivalryPreviews, setRivalryPreviews] = useState([]);

  useEffect(() => {
    loadSchedule(CURRENT_SEASON.year)
      .then((nextSchedule) => {
        setSchedule(nextSchedule);
        const nextMatchups = matchupsForWeek(nextSchedule, CURRENT_SEASON.currentWeek?.week);
        return Promise.all([
          fetch('/data/weekly_matchups.csv').then((response) => response.text()).then(parseCSV),
          loadCareerRankings(),
        ]).then(([weeklyRows, rankings]) => {
          setRivalryPreviews(buildWeekOneRivalryPreviews(nextMatchups, weeklyRows, rankings));
        });
      })
      .catch(() => setSchedule(null));
  }, []);

  const officialMatchups = matchupsForWeek(schedule, CURRENT_SEASON.currentWeek?.week);
  const knownCards = [
    {
      label: 'KEEPER RULE',
      title: 'One keeper per manager.',
      blurb: CURRENT_SEASON.keeperRule.summary,
      accent: 'gold',
    },
    {
      label: 'PICK COST',
      title: "The keeper costs last year's draft round.",
      blurb: CURRENT_SEASON.keeperRule.cost,
      accent: 'gold',
    },
    {
      label: 'DRAFT BOARD',
      title: 'The 2025 draft is now evidence.',
      blurb: 'The old draft board matters because it determines the price of every possible keeper.',
      accent: 'red',
    },
    {
      label: 'OPENING WEEK',
      title: 'The Week 1 schedule is official.',
      blurb: 'Yahoo has published all five opening-week matchups. Analysis waits for drafted rosters; the schedule does not.',
      accent: 'gold',
    },
  ];

  const unknownCards = [
    {
      title: 'Keeper choices',
      blurb: 'No manager keeper decisions are published yet.',
    },
    {
      title: 'Draft order',
      blurb: 'Yahoo has published all 17 rounds with traded picks applied; player selections remain pending.',
    },
    {
      title: 'Power rankings',
      blurb: 'No post-draft rankings until teams actually exist.',
    },
  ];

  return (
    <>
      <HeroBackground variant="gold" backdrop="studio" className="px-6 md:px-14 py-16">
        <Eyebrow>WEEKLY</Eyebrow>
        <h1 className="font-display font-black uppercase leading-[0.88] tracking-normal mt-3.5 mb-5" style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>
          PRE-DRAFT<br /><span className="text-accent-gold">WAR ROOM.</span>
        </h1>
        <p className="text-base md:text-lg text-ink-secondary leading-relaxed max-w-2xl font-light">
          Yahoo has published the opening-week schedule, but weekly analysis remains paused until
          keepers are locked and the league drafts. The matchup card below is official; projections,
          power rankings, and roster storylines will wait for real teams.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to="/draft-room"
            className="text-[11px] text-bg-base font-black tracking-[0.25em] bg-accent-gold px-5 py-3.5"
          >
            OPEN DRAFT ROOM →
          </Link>
          <Link
            to="/draft-room/2025"
            className="text-[11px] text-accent-gold font-extrabold tracking-[0.25em] border-[1.5px] border-accent-gold px-5 py-3"
          >
            2025 DRAFT RECEIPTS
          </Link>
        </div>
      </HeroBackground>

      <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-deep">
        <Reveal><SectionHeader eyebrow="WHAT IS LIVE" title="The keeper rule changes the draft before the draft starts." /></Reveal>
        <Reveal delay={80} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3.5">
          {knownCards.map((card) => (
            <article
              key={card.label}
              className={`bg-bg-surface p-6 border-t-[3px] ${
                card.accent === 'red' ? 'border-accent-red' : 'border-accent-gold'
              }`}
            >
              <div className={`text-[9px] tracking-[0.25em] font-extrabold mb-3 ${
                card.accent === 'red' ? 'text-accent-red' : 'text-accent-gold'
              }`}>
                ▾ {card.label}
              </div>
              <div className="text-lg font-black leading-tight mb-2 tracking-normal">{card.title}</div>
              <p className="text-[12px] md:text-[13px] text-ink-secondary leading-relaxed italic">
                {card.blurb}
              </p>
            </article>
          ))}
        </Reveal>
      </section>

      {hasPublishedSchedule() && (
        <section className="px-6 md:px-14 py-14 border-b border-border">
          <Reveal><SectionHeader eyebrow="OFFICIAL YAHOO SCHEDULE" title="Week 1 is on the board." /></Reveal>
          {schedule === undefined && (
            <p className="text-sm text-ink-tertiary italic">Loading the official schedule…</p>
          )}
          {schedule === null && (
            <p className="text-sm text-accent-red italic">The published schedule file could not be loaded.</p>
          )}
          {officialMatchups.length > 0 && (
            <Reveal delay={80} className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {officialMatchups.map((matchup, index) => (
                <MatchupCard
                  key={`${matchup.home}-${matchup.away}`}
                  label={`WEEK 1 · MATCHUP ${index + 1}`}
                  home={{ name: matchup.home, subtitle: matchup.homeTeam }}
                  away={{ name: matchup.away, subtitle: matchup.awayTeam }}
                  storyline="Official matchup. Preview and projection publish after the Aug 29 draft."
                />
              ))}
            </Reveal>
          )}
        </section>
      )}

      <WeekOneRivalryPreview previews={rivalryPreviews} />

      <section className="px-6 md:px-14 py-14 border-b border-border">
        <Reveal><SectionHeader eyebrow="WHAT REMAINS PENDING" title="The schedule is real. The teams are not drafted." eyebrowColor="red" /></Reveal>
        <Reveal delay={80} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {unknownCards.map((card) => (
            <article key={card.title} className="bg-bg-surface p-6 border-l-[3px] border-accent-red">
              <div className="text-[9px] tracking-[0.25em] font-extrabold mb-3 text-accent-red">
                PENDING
              </div>
              <div className="text-base font-black leading-tight mb-2 tracking-normal">{card.title}</div>
              <p className="text-[12px] text-ink-tertiary leading-relaxed">{card.blurb}</p>
            </article>
          ))}
        </Reveal>
      </section>

      <section className="px-6 md:px-14 py-14 bg-bg-deep">
        <Reveal><SectionHeader eyebrow="WHEN THIS UNLOCKS" title="After keepers and draft data are real." /></Reveal>
        <Reveal delay={80} className="bg-bg-surface p-6 md:p-8 max-w-3xl">
          <p className="text-sm md:text-base text-ink-secondary leading-relaxed italic">
            Once keepers are locked and the 2026 draft is complete, this page can become a real
            weekly preview again: actual matchups, actual rosters, actual draft fallout, and actual
            reasons for people to overreact.
          </p>
        </Reveal>
      </section>
    </>
  );
}
